import {
  HistoryDonationStatus,
  type HistoryFilterStore,
  type HistoryDonationStatusType,
} from './donationTypes';

export const HistoryStatus = {
  Collected: 'COLLECTED',
  NoShow: 'NO_SHOW',
  CancelledByStore: 'CANCELLED_BY_STORE',
  CancelledByCharity: 'CANCELLED_BY_CHARITY',
  Declined: 'DECLINED',
} as const;

export type HistoryStatusType = (typeof HistoryStatus)[keyof typeof HistoryStatus];

const HISTORY_STATUS_TO_API: Record<HistoryStatusType, HistoryDonationStatusType[]> = {
  [HistoryStatus.Collected]: [HistoryDonationStatus.Collected],
  [HistoryStatus.NoShow]: [HistoryDonationStatus.NoShow],
  [HistoryStatus.CancelledByStore]: [HistoryDonationStatus.CancelledByStore],
  [HistoryStatus.CancelledByCharity]: [HistoryDonationStatus.CancelledByCharity],
  [HistoryStatus.Declined]: [HistoryDonationStatus.Declined],
};

export const ALL_HISTORY_STATUSES = Object.values(HistoryStatus);

export const resolveHistoryStatuses = (
  filter?: string[] | null,
): HistoryDonationStatusType[] | null => {
  if (!filter) {
    return null;
  }
  return ALL_HISTORY_STATUSES.filter((status) => filter.includes(status)).flatMap(
    (status) => HISTORY_STATUS_TO_API[status],
  );
};

const API_STATUS_TO_HISTORY_STATUS = ALL_HISTORY_STATUSES.reduce<
  Partial<Record<HistoryDonationStatusType, HistoryStatusType>>
>((acc, status) => {
  HISTORY_STATUS_TO_API[status].forEach((apiStatus) => {
    acc[apiStatus] = status;
  });
  return acc;
}, {});

const isHistoryDonationStatus = (value: string): value is HistoryDonationStatusType =>
  value in API_STATUS_TO_HISTORY_STATUS;

export type DonationHistoryFilters = {
  stores: HistoryFilterStore[];
  statuses: HistoryStatusType[];
};

type DonationHistoryFiltersFromAPI = {
  stores: HistoryFilterStore[];
  statuses: string[];
};

export const toDonationHistoryFilters = ({
  stores,
  statuses,
}: DonationHistoryFiltersFromAPI): DonationHistoryFilters => {
  const groups = new Set(
    statuses
      .filter(isHistoryDonationStatus)
      .map((apiStatus) => API_STATUS_TO_HISTORY_STATUS[apiStatus]),
  );
  return {
    stores,
    statuses: ALL_HISTORY_STATUSES.filter((status) => groups.has(status)),
  };
};
